/**
 * EditorBulletPoints - A component for managing bullet point variants in product descriptions
 *
 * This component handles variant creation, editing, deletion, and prioritization for bullet points.
 * The first variant in the array is considered the "main" variant and may be displayed differently.
 * Bullet points can be part of either editorial or technical descriptions
 */
import { AddBox, CheckCircle } from "@mui/icons-material";

import {
  Box,
  Button,
  Tooltip,
  Typography,
  TextareaAutosize,
} from "@mui/material";
import { useNotification } from "@refinedev/core";
import { useEffect, useState } from "react";
import {
  addBulletPointVariant,
  removeBulletPointVariant,
} from "../../api/batches";
import { BulletPoint } from "../../pages/playground/types";

export default function EditorBulletPoints({
  bulletPoint,
  onRefresh,
  isAddingVariant,
  setIsAddingVariant,
  bulletPointToCopy,
  setBulletPointToCopy,
}: {
  bulletPoint: BulletPoint;
  onRefresh: () => void;
  isAddingVariant: boolean;
  setIsAddingVariant: (value: boolean) => void;
  bulletPointToCopy?: string | null;
  setBulletPointToCopy?: (value: string | null) => void;
}) {
  const { open } = useNotification();

  // Local state to handle optimistic UI updates before API calls complete
  const [localBulletPoint, setLocalBulletPoint] =
    useState<BulletPoint>(bulletPoint);

  // Sync local state with props when parent component updates
  useEffect(() => {
    setLocalBulletPoint(bulletPoint);
  }, [bulletPoint]);

  /**
   * Adds a new variant to the bullet point
   * New variants are added to the beginning of the list, becoming the main variant
   */
  const onAddBulletPointVariant = async (variant: string) => {
    if (variant.trim() === "") {
      return;
    }

    try {
      // Optimistically update UI with new variant at the beginning
      setLocalBulletPoint((prevState) => ({
        ...prevState,
        variants: [variant, ...prevState.variants],
      }));

      await addBulletPointVariant({
        bulletpointId: bulletPoint.id,
        variant,
      });

      await onRefresh();
    } catch (error) {
      console.error("Error adding variant:", error);

      open?.({
        message: "Failed to add variant. Please try again.",
        type: "error",
      });

      // Revert to original state if API call fails
      setLocalBulletPoint(bulletPoint);
    }
  };

  /**
   * Handles editing an existing variant
   * Implemented as remove + add operations in the backend
   */
  const onVariantEdit = async (oldVariant: string, newVariant: string) => {
    if (oldVariant === newVariant) {
      setIsEditingVariant(false);

      return;
    }

    try {
      setIsEditingVariant(false);

      // Optimistic UI update - replace old variant with new one and keep at front
      setLocalBulletPoint((prevState) => ({
        ...prevState,
        variants: [
          newVariant,
          ...prevState.variants.filter((v) => v !== oldVariant),
        ],
      }));

      await removeBulletPointVariant({
        bulletpointId: bulletPoint.id,
        variant: oldVariant,
      });

      await addBulletPointVariant({
        bulletpointId: bulletPoint.id,
        variant: newVariant,
      });

      await onRefresh();
    } catch (error) {
      console.error("Error updating variant:", error);

      open?.({
        message: "Failed to update variant. Please try again.",
        type: "error",
      });

      // Revert to original state if API call fails
      setLocalBulletPoint(bulletPoint);
      setIsEditingVariant(false);
    }
  };

  // UI state for adding and editing variants
  const [isEditingVariant, setIsEditingVariant] = useState<false | string>(
    false
  );
  const [editingVariantValue, setEditingVariantValue] = useState("");

  // Use local variants for rendering to enable optimistic UI updates
  const variants = localBulletPoint.variants;
  const emptyVariants = variants.length === 0;

  const variantBgColor = "#ffffff";
  const variantColor = "#404040";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "0.5rem",
      }}
    >
      {variants.map((variant, i) => {
        return (
          <Box
            key={`${bulletPoint.type}-${variant}-${i}`}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "0 0.15rem",
              backgroundColor: variantBgColor,
              userSelect: "none",
            }}
            onClick={() => {
              // Single click to enter edit mode
              if (!isEditingVariant) {
                setIsEditingVariant(variant);
                setEditingVariantValue(variant);
              }
            }}
          >
            {isEditingVariant === variant ? (
              <Box sx={{ width: "100%", position: "relative" }}>
                <TextareaAutosize
                  autoFocus
                  value={editingVariantValue}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setEditingVariantValue(e.target.value);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === "Enter") {
                      // Use Ctrl+Enter to save, allowing regular Enter for newlines
                      e.preventDefault();
                      onVariantEdit(isEditingVariant, editingVariantValue);
                    }

                    if (e.key === "Escape") {
                      setIsEditingVariant(false);
                    }
                  }}
                  onBlur={() => {
                    onVariantEdit(isEditingVariant, editingVariantValue);
                  }}
                  style={{
                    width: "100%",
                    fontSize: "0.80rem",
                    resize: "none",
                    outline: "none",
                    fontFamily: "inherit",
                    paddingRight: "30px", // Space for the save button
                    border: "none",
                    backgroundColor: "transparent",
                    color: variantColor,
                  }}
                />
                <Tooltip title="Save">
                  <CheckCircle
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        color: variantColor,
                      },
                      color: variantColor,
                      fontSize: "1.2rem",
                      position: "absolute",
                      right: "2px",
                      top: "4px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onVariantEdit(isEditingVariant, editingVariantValue);
                    }}
                  />
                </Tooltip>
              </Box>
            ) : (
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  fontSize: "0.80rem",
                  color: variantColor,
                }}
              >
                {variant}
              </Typography>
            )}
          </Box>
        );
      })}

      {isAddingVariant && (
        // Input field for adding new variants
        <Box sx={{ width: "100%", position: "relative" }}>
          <TextareaAutosize
            autoFocus
            value={editingVariantValue}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setEditingVariantValue(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setIsAddingVariant(false);
                onAddBulletPointVariant(editingVariantValue);
              }

              if (e.key === "Escape") {
                setIsAddingVariant(false);
              }

              // Cancel adding mode on empty backspace
              if (e.key === "Backspace" && editingVariantValue.length === 0) {
                setIsAddingVariant(false);
              }
            }}
            onBlur={() => {
              setIsAddingVariant(false);
              onAddBulletPointVariant(editingVariantValue);
            }}
            style={{
              width: "100%",
              fontSize: "0.80rem",
              resize: "none",
              outline: "none",
              fontFamily: "inherit",
              paddingRight: "30px", // Space for the save button
              border: "none",
              backgroundColor: "transparent",
              color: variantColor,
            }}
          />
          <Tooltip title="Salva">
            <CheckCircle
              sx={{
                cursor: "pointer",
                "&:hover": {
                  color: "green",
                },
                fontSize: "1.2rem",
                position: "absolute",
                right: "0",
                top: "0",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsAddingVariant(false);
                onAddBulletPointVariant(editingVariantValue);
              }}
            />
          </Tooltip>
        </Box>
      )}

      {emptyVariants && !isAddingVariant && (
        <AddBox
          sx={{
            fontSize: "1rem",
            cursor: "pointer",
            marginTop: "0.1rem",
            color: "gray",
            "&:hover": {
              color: "blue",
              opacity: 1,
            },
          }}
          onClick={() => setIsAddingVariant(true)}
        />
      )}

      {/* Only allow copying when variants are empty (single variant mode) */}
      {bulletPointToCopy && emptyVariants && (
        <Button
          size="small"
          variant="contained"
          color="success"
          sx={{
            fontSize: "0.5rem",
          }}
          onClick={async () => {
            await addBulletPointVariant({
              bulletpointId: bulletPoint.id,
              variant: String(bulletPointToCopy).trim(),
            });

            if (setBulletPointToCopy) {
              await setBulletPointToCopy(null);
            }

            await onRefresh();
          }}
        >
          Copy here
        </Button>
      )}
    </Box>
  );
}
